import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterContentInit, AfterViewInit, ContentChild, ElementRef, EventEmitter, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, Input, OnChanges, SimpleChanges, Output } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConfirmDialogData } from '../confirm-dialog/confirm-dialog-data';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

// export enum MessageType {
//   normal,
//   success,
//   error,
//   info,
//   warning
// }

export type MessageType = 'normal' | 'success' | 'error' | 'info' | 'warning' | undefined;

export interface ScrollOverflowEvent {
  deltaY: number;
}

type YOverflowStatus = 'TOP' | 'BOTTOM' | 'NONE' | 'CONTAINED';

@Component({
    selector: 'app-message-bar',
    templateUrl: './message-bar.component.html',
    styleUrls: ['./message-bar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        // trigger('toggleTrigger', [
        //   transition(':enter', [ // alias for void => *
        //     style({ transform: 'scaleY(0.1)', 'transform-origin': 'left top', height: '0px' }),
        //     animate('200ms', style({ transform: 'scaleY(1)', height: 'auto' })),
        //   ]),
        //   transition(':leave', [ // alias for * => void
        //     style({ 'transform-origin': 'left top' }),
        //     animate('100ms', style({ height: '0px', transform: 'scaleY(0.1)' }))
        //   ])
        // ]),
        trigger('toggleTrigger', [
            transition(':enter', [
                style({ transform: 'scaleY(0.1)', 'transform-origin': 'left top', height: '0px' }),
                animate('200ms', style({ transform: 'scaleY(1)', height: 'auto' })),
            ]),
            transition(':leave', [
                style({ 'transform-origin': 'left top' }),
                animate('100ms', style({ height: '0px', transform: 'scaleY(0.1)' }))
            ]),
            state('open', style({ transform: 'scaleY(1)', height: 'auto' })),
            state('minified', style({ height: '20px' })),
            transition('open => minified', [
                style({ 'transform-origin': 'left top' }),
                animate('100ms')
            ]),
            transition('minified => open', [
                style({ 'transform-origin': 'left top' }),
                animate('100ms')
            ]),
        ])
    ],
    standalone: false
})
export default class MessageBarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() message: string;

  // Display State
  @Input() isShown: boolean = true;
  @Input() canClose: boolean = true;
  @Input() minifyOnClose: boolean = false;
  @Input() showMessagePopupOnClick: boolean = false;
  @Output() closed = new EventEmitter();

  // Styles customization
  @Input() colorTheme: ThemePalette = undefined;
  @Input() cssClass: string = undefined;
  @Input() messageType: MessageType = 'normal';

  @Output() scrollOverflow = new EventEmitter<ScrollOverflowEvent>();

  @ContentChild('content') content: ElementRef;
  @ViewChild('contentContainer') contentContainer: ElementRef;

  private _contentContainerWheelListener = () => { };

  constructor(
    private _dialog: MatDialog,
    private _renderer: Renderer2
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.colorTheme && this.messageType === 'normal') {
      this.messageType = undefined;
    }
    if(changes["message"] && changes["message"].currentValue) {
      this.message = this.sanitizeMessage(changes["message"].currentValue);
    }
  }

  private sanitizeMessage(message: string): string {
    //remove <br> tags (line breaks are useless because style is "white-space: pre-wrap;")
    return message.replace(/<br\s*\/?>/gi, '');
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    //TODO  this.ngZone.runOutsideAngular(() => {  //see https://medium.com/claritydesignsystem/four-ways-of-listening-to-dom-events-in-angular-part-3-renderer2-listen-14c6fe052b59
    this._contentContainerWheelListener = this._renderer.listen(this.contentContainer.nativeElement, 'wheel', this.onMouseWheelHandler.bind(this));
  }

  private onMouseWheelHandler(e: WheelEvent) {
    // console.log('messageBar onMouseWheelHandler', e);
    this.computeAndEmitScrollOverflowEvent(-e.deltaY);
  }

  onPan(e: Event) {
    // TODO regrouper les deltas et les envoyer en une fois
    const event = e as unknown as HammerInput;

    //HACK exclude final event with center à zero (strange bhavior on Chrome)
    if (event.isFinal && event.center.x === 0 && event.center.y === 0) {
      // console.log(`messageBar onPan EXCLUDED deltaY ${event.deltaY}`, event);
    }
    else {
      // console.log(`messageBar onPan deltaY ${event.deltaY}`, event);
      this.computeAndEmitScrollOverflowEvent(event.deltaY);
    }
  }

  private computeAndEmitScrollOverflowEvent(delta: number) {
    const contentContainerYOverflowStatus = this.getContentContainerYOverflowStatus();
    if ((contentContainerYOverflowStatus == 'TOP' && delta > 0)
      || (contentContainerYOverflowStatus == 'BOTTOM' && delta < 0)
      || (contentContainerYOverflowStatus == 'CONTAINED' && delta !== 0)
    ) {
      //console.log('messageBar emit scrollOverflow', delta);
      this.scrollOverflow.emit({ deltaY: delta });
    }
  }

  private getContentContainerYOverflowStatus(): YOverflowStatus {
    const contentContainer = this.contentContainer.nativeElement as HTMLDivElement;

    const scrollTopOffset = Math.round(contentContainer.scrollTop);
    const scrollBottomOffset = Math.round(contentContainer.scrollTop + contentContainer.clientHeight);
    // seuil de détection de débordement
    const scrollBoundaryThreshold = Math.ceil(contentContainer.clientHeight / 5);

    const isScrolledToTop = (scrollTopOffset < scrollBoundaryThreshold)
    const isScrolledToBottom = (scrollBottomOffset >= (contentContainer.scrollHeight - scrollBoundaryThreshold));

    const yOverflowStatus = (isScrolledToTop && !isScrolledToBottom)
      ? 'TOP'
      : (isScrolledToBottom && !isScrolledToTop)
        ? 'BOTTOM'
        : (isScrolledToTop && isScrolledToBottom)
          ? 'CONTAINED'
          : 'NONE';

    //console.log(yOverflowStatus, scrollTopOffset, contentContainer.scrollHeight, contentContainer.clientHeight);

    return yOverflowStatus;
  }


  ngOnDestroy(): void {
    // (this.content.nativeElement as HTMLDivElement).removeEventListener('scroll',
    this._contentContainerWheelListener?.apply(this);
  }

  close() {
    this.isShown = false;

    this.closed.emit();
  }

  private getMessageHtmlContent(): string {
    // this.content?.nativeElement.hasChildNodes()
    return this.content?.nativeElement.innerHTML || this.message;
  }

  onMessageClick() {
    if ((this.minifyOnClose && !this.isShown) && this.showMessagePopupOnClick) {
      this.showMessagePopup();
    }
  }

  private showMessagePopup() {

    setTimeout(() => {
      this._dialog.open(ConfirmDialogComponent, {
        data: {
          message: this.getMessageHtmlContent(),
          showDenyButton: false,
          confirmButtonLabel: 'COMMON.CLOSE'
        } as ConfirmDialogData
      });
    }, 200);

  }

}
